import { useEffect, useMemo, useState } from "react";
import worklistApi from "../services/worklistApi";
import { ColumnConfig, Row } from "../types";
import { formatCell, humanizeKey, isAbort, rowKey, rowText } from "../utils";
import NextStepButton from "./NextStepButton";
import RequisitionStatusBadge from "./RequisitionStatusBadge";
import ResultFileLink from "./ResultFileLink";
import { Loader } from "Shared/Common/Loader";
import useLang from "Shared/hooks/useLanguage";

interface Props {
  row: Row;
  tabId: number;
  // All columns of the tab (used to turn SQL aliases into labels).
  columns: ColumnConfig[];
  refreshKey: number;
  busy: boolean;
  onClose: () => void;
  onNextStep: (row: Row, file?: File) => void;
  onOpenFile: (url: string) => void;
  onView: (row: Row) => void;
}

// Shown in the header (patient name · accession # · status), so not repeated in the sections.
const HEADER_KEYS = [
  "firstname",
  "lastname",
  "patientname",
  "accessionnumber",
  "requisitionstatus",
];
// Internal ids / action fields.
const HIDDEN_KEYS = [
  "requisitionorderid",
  "requisitionid",
  "recordid",
  "requisitiontypeid",
  "nextstep",
];

// First matching rule wins; anything else falls under "Order".
const SECTIONS: { title: string; icon: string; match: RegExp }[] = [
  { title: "Insurance", icon: "bi-shield-check", match: /insurance|payer|policy|subscriber|guarantor/i },
  { title: "Patient", icon: "bi-person", match: /patient|firstname|lastname|middlename|birth|dob|gender|sex|mrn|address|phone|email|race|ethnic/i },
  { title: "Specimen & Collection", icon: "bi-droplet", match: /collect|specimen|receive|sample|tube|panel|test|result/i },
  { title: "Provider", icon: "bi-hospital", match: /physician|facility|lab|provider|clinic|doctor/i },
  { title: "Audit", icon: "bi-clock-history", match: /added|created|updated|modified|by$/i },
];
const DEFAULT_SECTION = { title: "Order", icon: "bi-file-earmark-medical" };

// Side panel: loads one row with all heavy joins via GET {id}/Detail.
const DetailPanel = ({
  row,
  tabId,
  columns,
  refreshKey,
  busy,
  onClose,
  onNextStep,
  onOpenFile,
  onView,
}: Props) => {
  const { t } = useLang();
  const id = rowKey(row);
  const [detail, setDetail] = useState<Row | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "notfound" | "error">(
    "loading"
  );

  useEffect(() => {
    const ac = new AbortController();
    setState("loading");
    worklistApi
      .detail(id, tabId, ac.signal)
      .then((d) => {
        setDetail(d);
        setState(d ? "ok" : "notfound");
      })
      .catch((e) => {
        if (!isAbort(e)) setState("error");
      });
    return () => ac.abort();
  }, [id, tabId, refreshKey]);

  useEffect(() => setDetail(null), [id]);

  const labelFor = useMemo(() => {
    return (key: string) => {
      const k = key.toLowerCase();
      const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const alias = new RegExp(`\\sas\\s+\\[?${escaped}\\]?\\s*$`, "i");
      return (
        columns.find(
          (c) =>
            [c.key, c.columnValue].some((x) => x?.toLowerCase() === k) ||
            alias.test(c.columnName ?? "")
        )?.label ?? humanizeKey(key)
      );
    };
  }, [columns]);

  // Header uses the freshest data available (detail merged over the grid row).
  const data: Row = detail ? { ...row, ...detail } : row;
  const patientName =
    [rowText(data, "FirstName"), rowText(data, "LastName")]
      .filter(Boolean)
      .join(" ") || rowText(data, "PatientName");
  const accession = rowText(data, "AccessionNumber");
  const order = rowText(data, "Order");
  const status = rowText(data, "RequisitionStatus");
  const requisitionId = rowText(data, "RequisitionId");

  // Group the detail fields into sections (empty values show as "—").
  const sections = useMemo(() => {
    const groups = new Map<string, { icon: string; fields: [string, unknown][] }>();
    Object.entries(detail ?? {}).forEach(([k, v]) => {
      const lk = k.toLowerCase();
      if (k.startsWith("__") || HEADER_KEYS.includes(lk) || HIDDEN_KEYS.includes(lk))
        return;
      const section = SECTIONS.find((s) => s.match.test(k)) ?? DEFAULT_SECTION;
      const g = groups.get(section.title) ?? { icon: section.icon, fields: [] };
      g.fields.push([k, v]);
      groups.set(section.title, g);
    });
    const sectionOrder = [
      DEFAULT_SECTION.title,
      ...SECTIONS.map((s) => s.title),
    ];
    return sectionOrder
      .filter((title) => groups.has(title))
      .map((title) => ({ title, ...groups.get(title)! }));
  }, [detail]);

  const renderValue = (key: string, value: unknown) => {
    const text = formatCell(value);
    if (!text) return <span className="text-gray-400">—</span>;
    const k = key.toLowerCase();
    if (k === "resultfile")
      return <ResultFileLink url={text} onOpen={onOpenFile} />;
    if (k === "requisitionstatus") return <RequisitionStatusBadge label={text} />;
    return text;
  };

  return (
    <aside
      className="vr2-detail"
      aria-label={t("Requisition detail")}
      id="ViewRequisitionV2Detail"
    >
      {/* Header: same height/look as the table header row — patient · accession · status */}
      <div className="vr2-detail-head">
        <span className="vr2-head-name" title={patientName}>
          {patientName || order || `${t("Order")} ${id}`}
        </span>
        {accession && (
          <>
            <span className="vr2-head-sep" aria-hidden="true" />
            <span className="vr2-head-accession" title={t("Accession #")}>
              {accession}
            </span>
          </>
        )}
        {status && (
          <>
            <span className="vr2-head-sep" aria-hidden="true" />
            <RequisitionStatusBadge label={status} />
          </>
        )}
        <button
          type="button"
          className="btn btn-icon btn-sm h-28px w-28px btn-active-light-primary ms-auto flex-shrink-0"
          aria-label={t("Close")}
          onClick={onClose}
        >
          <i className="bi bi-x-lg fs-6"></i>
        </button>
      </div>

      {/* Body: grouped fields */}
      <div className="vr2-detail-body">
        {state === "loading" && !detail && (
          <div className="py-10">
            <Loader />
          </div>
        )}
        {state === "notfound" && (
          <div className="text-center text-gray-600 py-10">
            <i className="bi bi-inbox fs-2x d-block mb-2"></i>
            {t("This requisition is no longer available.")}
          </div>
        )}
        {state === "error" && (
          <div className="alert alert-danger py-2 my-3">
            {t("Could not load details.")}
          </div>
        )}

        {detail &&
          state !== "notfound" &&
          sections.map((s) => (
            <section key={s.title} className="vr2-section">
              <div className="vr2-section-title">
                <i className={`bi ${s.icon}`}></i>
                {t(s.title)}
              </div>
              <dl className="vr2-fields">
                {s.fields.map(([k, v]) => (
                  <div key={k} className="vr2-field">
                    <dt>{t(labelFor(k))}</dt>
                    <dd>{renderValue(k, v)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
      </div>

      {/* Footer: actions */}
      <div className="vr2-detail-foot">
        <NextStepButton row={data} busy={busy} onNextStep={onNextStep} />
        {requisitionId && (
          <button
            type="button"
            className="btn btn-sm btn-light-primary ms-auto"
            onClick={() => onView(data)}
          >
            <i className="bi bi-box-arrow-up-right me-1"></i>
            {t("Open Order View")}
          </button>
        )}
      </div>
    </aside>
  );
};

export default DetailPanel;
